import { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { taskRowBadge, taskStatusBadge } from '../../../lib/task-status';
import styles from './Badge.module.css';

// THE status/category label for the whole app — Figma "⚠️ Symphony DS" → Badge
// Symphony DS (node 434:337). Never hand-roll a small filled pill with 12px semibold
// text; see CLAUDE.md → "Badges".
//
// The design is Style × Size, plus the two icon slots, so those are the prop names:
//   variant  → Figma *Style*  (its axis is called Style, which `style` already means
//              in React — the value names are the design system's own)
//   size     → Figma *Size*   (tiny 20 · small 24 · medium 28)
//   startIcon / endIcon → Figma *Show prefix* / *Show suffix*
//
// A badge takes a STYLE, never a colour: each Style owns a `--ds-badge-*` fill/ink/
// border triple, so hover-free states and dark mode are the token layer's problem and
// no call site can invent a hue. It is also not interactive — a clickable pill is a
// <Button>, and a count on a tab is a counter, not a Badge.
export type BadgeVariant =
  | 'waiting-on-a-reply'
  | 'waiting-on-you'
  | 'in-progress'
  | 'done'
  | 'paused'
  | 'failed'
  | 'connected'
  | 'coming-soon'
  | 'active'
  | 'recommended'
  | 'agents'
  | 'agent-profession'
  | 'this-week'
  | 'next-week'
  | 'in-three-weeks';

export type BadgeSize = 'tiny' | 'small' | 'medium';

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  /** Figma's Style. The design system's grey fill is `waiting-on-a-reply`, its only outline is `agent-profession`. */
  variant?: BadgeVariant;
  /** Figma's Size. Default `tiny` — the size every badge in the app draws at. */
  size?: BadgeSize;
  label?: ReactNode;
  /** Figma's "Show prefix" — a 16px leading icon. */
  startIcon?: ReactNode;
  /** Figma's "Show suffix" — a 16px trailing icon. */
  endIcon?: ReactNode;
  /**
   * Opt-in HOVER state (Figma "Badge hovers", 20102-60161): on `:hover` the pill lightens its fill and
   * darkens its ink, per Style. Badges are inert by default — this is only for a badge used as an actual
   * control (the Overview status picker's options). The hover colours live in the Badge's own CSS so a
   * call site never overrides `--ds-badge-*` by hand.
   */
  hoverable?: boolean;
  children?: ReactNode;
}

export function Badge({ variant = 'waiting-on-a-reply', size = 'tiny', label, startIcon, endIcon, hoverable, className, children, ...rest }: BadgeProps) {
  const classes = [styles.root, styles[`variant-${variant}`], styles[`size-${size}`], hoverable && styles.hoverable, className].filter(Boolean).join(' ');

  return (
    <span className={classes} {...rest}>
      {startIcon != null && (
        <span aria-hidden className={styles.icon}>
          {startIcon}
        </span>
      )}
      <span className={styles.label}>{label ?? children}</span>
      {endIcon != null && (
        <span aria-hidden className={styles.icon}>
          {endIcon}
        </span>
      )}
    </span>
  );
}

// The task-status badge: reads the shared status→{labelKey,variant} map and localizes
// the label at the render site (mirrors the native TaskScreen). The map hands out a
// STYLE, never a colour — see lib/task-status.ts.
export function StatusBadge({
  status,
  size,
  className,
  detail,
  notStarted,
  awaitingDecision,
  outreach,
  inMotion,
}: {
  status: string;
  size?: BadgeSize;
  className?: string;
  // The task-DETAIL screen sets this so IN_REVIEW reads "In review"; cards leave it off (bucket label).
  detail?: boolean;
  /** This row is laid out and never started (`isNotStarted`) — it reads "Not started", not "In progress".
   *  Passed in rather than derived here: the rule needs the whole task, and this component takes a status. */
  notStarted?: boolean;
  /** This row is blocked on an OPEN question the reader has to answer (`awaitsDecision`) — it reads
   *  "Action needed", whatever its status is. Same reason as `notStarted`: the rule needs the whole task
   *  and this component takes a status string. Without it a row that the bucket rule files under Action
   *  needed would sit in that tab wearing an "In progress" chip. */
  awaitingDecision?: boolean;
  /** This row is an inbound OUTREACH, not an AgentTask — while it is still the reader's turn the badge
   *  names the ask ("Reply") instead of restating the bucket. Passed in for the same reason as the two
   *  above: the caller knows the row's kind, this component only sees a status string. */
  outreach?: boolean;
  /** An agent is executing on this row RIGHT NOW (`isInMotion`) — it reads "Working" rather than the
   *  bucket's "Active Task". Passed in for the same reason as the three above: the rule needs the whole
   *  task (a live run, a recent heartbeat), and this component only ever sees a status string.
   *  Tri-state at the source: absent means "not checked", never "not working". */
  inMotion?: boolean;
}) {
  const { t } = useTranslation();
  const badge = taskStatusBadge(status, { detail, notStarted, awaitingDecision, outreach, inMotion });
  return <Badge variant={badge.variant} label={t(badge.labelKey)} size={size} className={className} />;
}

/**
 * The same chip, derived from the whole TASK rather than from a status plus four hand-passed flags.
 *
 * Prefer this wherever the row is in hand. `StatusBadge`'s flags are each a rule that needs the whole
 * task, so every call site is a chance to omit one — and an omitted flag renders a plausible-looking
 * wrong chip that no reviewer can spot from the diff. That is how "Working" shipped once with no render
 * site passing `inMotion`: correct rule, correct label, invisible on every screen.
 */
export function TaskRowBadge({
  task,
  size,
  className,
  detail,
}: {
  task: Parameters<typeof taskRowBadge>[0];
  size?: BadgeSize;
  className?: string;
  detail?: boolean;
}) {
  const { t } = useTranslation();
  const badge = taskRowBadge(task, { detail });
  return <Badge variant={badge.variant} label={t(badge.labelKey)} size={size} className={className} />;
}
